import { FC, useEffect, useState } from 'react';
import { useFetchFilters } from 'hooks/useFetchFilters';
import { useFetchQa } from 'modules/Workspace/hooks/useFetchQa';
import { usePostQa } from 'modules/Workspace/hooks/usePostQa';
import { usePutQa } from 'modules/Workspace/hooks/usePutQa';
import { useDeleteQa } from 'modules/Workspace/hooks/useDeleteQa';
import { usePostQaDocument } from 'modules/Workspace/hooks/usePostQaDocument';
import { DocumentCategory, QAItem as QaItemType } from 'types/api';
import { Box } from 'ui/Box';
import { QAItem } from '../QAItem';
import { AddQAForm } from '../AddQAForm';
import { QAButtons } from '../QAButtons';
import { AddDocumentLinkModal } from '../AddDocumentLinkModal';
import { DeleteModal } from 'components/DeleteModal';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Loader } from 'components/Loader';
import { Typography } from 'ui/Typography';

export const ManageQaPage: FC = () => {
  const [isAddQAOpened, setIsAddQAOpened] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [documentLink, setDocumentLink] = useState('');
  const [isAddDocumentLinkModalOpened, setIsAddDocumentLinkModalOpened] =
    useState(false);
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);
  const [qaList, setQaList] = useState<QaItemType[]>([]);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [departments, setDepartments] = useState<
    Omit<DocumentCategory, 'created_at'>[]
  >([]);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [editItem, setEditItem] = useState<QaItemType>();

  const { fetchQa, qa, status: fetchQaStatus, totalItems } = useFetchQa();
  const { postQa, status: postQaStatus, item, setPostStatus } = usePostQa();
  const { status: postDocumentStatus, postQaDocument } = usePostQaDocument();
  const { putQa, status: putQaStatus, response } = usePutQa();
  const { deleteQa, status: deleteStatus } = useDeleteQa();
  const [deleteQaCount, setDeleteQaCount] = useState(0);
  const [metaTotalItems, setMetaTotalItems] = useState(0);
  const {
    fetchCategories,
    departments: departmentsData,
    categories: categoriesData,
    status: categoryStatus,
  } = useFetchFilters();

  useEffect(() => {
    if (fetchQaStatus === 'idle') fetchQa();
    if (categoryStatus === 'idle') fetchCategories();
  }, []);

  useEffect(() => {
    if (postDocumentStatus === 'success') {
      setDocumentLink('');
      setIsAddQAOpened(false);
      fetchQa();
    }
  }, [postDocumentStatus]);

  useEffect(() => {
    if (categoryStatus === 'success') {
      setCategories(categoriesData);
      setDepartments(departmentsData);
    }
  }, [categoriesData, categoryStatus]);

  useEffect(() => {
    if (fetchQaStatus === 'success' && postDocumentStatus === 'success') {
      setQaList(qa);
      setMetaTotalItems(totalItems);
      return;
    }
    if (fetchQaStatus === 'success') setQaList((prev) => [...prev, ...qa]);
  }, [fetchQaStatus, qa]);

  useEffect(() => {
    if (postQaStatus === 'success') {
      setIsAddQAOpened(false);
      setQaList((prev) => [item as QaItemType, ...prev]);
      setPostStatus('idle');
    }
  }, [postQaStatus, item]);

  useEffect(() => {
    if (putQaStatus === 'success') {
      setIsAddQAOpened(false);
      setQaList((prev) =>
        prev.map((qa) => (qa.id === response?.id ? { ...qa, ...response } : qa))
      );
      setEditItem(undefined);
    }
  }, [putQaStatus, response]);

  useEffect(() => {
    if (deleteStatus === 'success') {
      setIsDeleteModalOpened(false);
      setQaList((prev) => prev.filter((qa) => qa.id !== deleteId));
      setDeleteQaCount((prev) => prev + 1);
      setMetaTotalItems((prev) => prev - 1);
      setDeleteId(null);
    }
  }, [deleteStatus]);

  useEffect(() => {
    if (deleteQaCount > 5) {
      setCurrentPage(1);
      setQaList([]);
      fetchQa(1);
      setDeleteQaCount(0);
    }
  }, [deleteQaCount]);

  const handleDelete = (id: number) => {
    setDeleteId(id);
    setIsDeleteModalOpened(true);
  };

  const handleEdit = (id: number) => {
    setEditId(id);
    const item = qaList.find((qa) => qa.id === id);
    if (item) {
      setEditItem(item);
      setIsAddQAOpened(true);
    }
  };

  const handleDeleteConfirm = () => {
    deleteId && deleteQa(deleteId);
  };

  const handleFormSubmit = ({
    answer,
    category,
    question,
    department,
  }: {
    answer: string;
    question: string;
    category: number;
    department: number;
  }) => {
    if (editId) {
      putQa(editId, { question, answer, category, department });
      setEditId(null);
      setEditItem(undefined);
    } else if (documentLink) {
      postQaDocument(documentLink, category, department);
    } else {
      postQa({ question, answer, category, department });
    }
  };

  const onNextPage = () => {
    setCurrentPage((prev) => prev + 1);
    fetchQa(currentPage + 1);
  };

  return (
    <Box className="flex flex-1 flex-col overflow-auto rounded-b-[16px] bg-white p-5 md:p-8">
      <AddDocumentLinkModal
        isModalOpen={isAddDocumentLinkModalOpened}
        onAddDocument={(url) => {
          setDocumentLink(url);
          setIsAddQAOpened(true);
        }}
        onClose={() => setIsAddDocumentLinkModalOpened(false)}
      />
      <DeleteModal
        disabled={deleteStatus === 'loading'}
        title="Are you sure you want to delete this question?"
        open={isDeleteModalOpened}
        onClose={() => setIsDeleteModalOpened(false)}
        onConfirm={handleDeleteConfirm}
      />
      {isAddQAOpened ? (
        <AddQAForm
          onAddQAOpen={() => setIsAddQAOpened(true)}
          editItem={editItem}
          onSubmit={handleFormSubmit}
          onAddQAClose={() => {
            setIsAddQAOpened(false);
            setDocumentLink('');
            setEditItem(undefined);
          }}
          onUploadDocument={() => setIsAddDocumentLinkModalOpened(true)}
          url={documentLink}
          categories={categories}
          departments={departments}
          disabled={
            postQaStatus === 'loading' ||
            putQaStatus === 'loading' ||
            postDocumentStatus === 'loading'
          }
        />
      ) : (
        <Box className="flex h-full flex-col">
          <QAButtons
            onAddQAOpen={() => setIsAddQAOpened(true)}
            onUploadDocument={() => setIsAddDocumentLinkModalOpened(true)}
          />
          <Box
            className="flex max-h-[calc(100vh-300px)] max-w-full flex-1 flex-col gap-4 overflow-y-auto"
            id="qa-list"
          >
            <InfiniteScroll
              dataLength={qaList.length}
              next={onNextPage}
              hasMore={qaList.length < metaTotalItems}
              loader={<Loader />}
              className="mt-6 flex flex-1 flex-col gap-4"
              scrollableTarget="qa-list"
              endMessage={
                qaList.length > 6 ? (
                  <Typography className="p-2 text-center text-mult">
                    No more QA
                  </Typography>
                ) : null
              }
            >
              {qaList.map(({ id, category, department, ...rest }) => (
                <QAItem
                  key={id}
                  onDelete={() => handleDelete(id)}
                  onEdit={() => handleEdit(id)}
                  category={category?.name || ''}
                  department={department !== null ? department.name : ''}
                  {...rest}
                />
              ))}
            </InfiniteScroll>
          </Box>
        </Box>
      )}
    </Box>
  );
};
